import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import type {
  ContactAssignmentConfirmPayload,
  ContactAssignmentConfirmResult,
  ContactAssignmentPreviewPayload,
  ContactAssignmentPreviewResult,
  ContactAssignmentRevokePayload,
} from "../types";

type ContactAssignmentPreviewItemRaw = {
  contact_id: number;
  contact_name: string;
  is_active: boolean;
  current_primary_user_id?: number | null;
  current_primary_user_name?: string | null;
  action: "assign" | "overwrite" | "share" | "skip";
  reason: string;
};

type ContactAssignmentPreviewResultRaw = {
  total_count: number;
  assignable_count: number;
  skipped_count: number;
  conflict_count: number;
  disabled_count: number;
  items: ContactAssignmentPreviewItemRaw[];
};

type ContactAssignmentConfirmResultRaw = {
  updated_count: number;
  skipped_count: number;
  log_count: number;
};

/** 预览联系人主销售或共享销售分配影响。 */
export async function previewContactAssignment(
  payload: ContactAssignmentPreviewPayload,
): Promise<ContactAssignmentPreviewResult> {
  const response = await httpClient.post<ApiEnvelope<ContactAssignmentPreviewResultRaw>>(
    "/crm/object-assignment/contacts/preview",
    mapPreviewPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人分配预览接口返回了无效数据");
  }
  return mapPreviewResult(response.data.data);
}

/** 确认联系人主销售或共享销售分配。 */
export async function confirmContactAssignment(
  payload: ContactAssignmentConfirmPayload,
): Promise<ContactAssignmentConfirmResult> {
  const response = await httpClient.post<ApiEnvelope<ContactAssignmentConfirmResultRaw>>(
    "/crm/object-assignment/contacts/confirm",
    mapConfirmPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人分配确认接口返回了无效数据");
  }
  return {
    updatedCount: response.data.data.updated_count,
    skippedCount: response.data.data.skipped_count,
    logCount: response.data.data.log_count,
  };
}

/** 撤销联系人共享销售关系。 */
export async function revokeContactSharedOwner(
  payload: ContactAssignmentRevokePayload,
): Promise<ContactAssignmentConfirmResult> {
  const response = await httpClient.post<ApiEnvelope<ContactAssignmentConfirmResultRaw>>(
    "/crm/object-assignment/contacts/revoke-shared-owner",
    {
      contact_ids: payload.contactIds,
      target_user_id: payload.targetUserId,
      current_user_id: payload.currentUserId,
      reason: payload.reason,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("撤销联系人共享销售接口返回了无效数据");
  }
  return {
    updatedCount: response.data.data.updated_count,
    skippedCount: response.data.data.skipped_count,
    logCount: response.data.data.log_count,
  };
}

/** 映射联系人分配预览请求为后端字段命名。 */
function mapPreviewPayload(payload: ContactAssignmentPreviewPayload) {
  return {
    contact_ids: payload.contactIds,
    target_user_id: payload.targetUserId,
    relation_type: payload.relationType,
    conflict_strategy: payload.conflictStrategy,
  };
}

/** 映射联系人分配确认请求为后端字段命名。 */
function mapConfirmPayload(payload: ContactAssignmentConfirmPayload) {
  return {
    ...mapPreviewPayload(payload),
    current_user_id: payload.currentUserId,
    permission_level: payload.permissionLevel,
    source_type: payload.sourceType,
    source_id: payload.sourceId,
    reason: payload.reason,
    source_snapshot_json: payload.sourceSnapshotJson,
  };
}

/** 映射联系人分配预览结果为前端字段命名。 */
function mapPreviewResult(raw: ContactAssignmentPreviewResultRaw): ContactAssignmentPreviewResult {
  return {
    totalCount: raw.total_count,
    assignableCount: raw.assignable_count,
    skippedCount: raw.skipped_count,
    conflictCount: raw.conflict_count,
    disabledCount: raw.disabled_count,
    items: (raw.items ?? []).map((item) => ({
      contactId: item.contact_id,
      contactName: item.contact_name,
      isActive: item.is_active,
      currentPrimaryUserId: item.current_primary_user_id ?? undefined,
      currentPrimaryUserName: item.current_primary_user_name ?? undefined,
      action: item.action,
      reason: item.reason,
    })),
  };
}
